import React from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import type { StatisticsSummaryView } from '../../api/generated/models/StatisticsSummaryView';
import { getDisplayCurrency, getAmountForCurrency, formatMoney } from '../../utils/statisticsHelpers';

interface SalesStrengthByHoursWidgetProps {
  data: StatisticsSummaryView | null;
}

/**
 * ТЗ п.9: Sales Strength by Hours - bar chart showing hourly sales activity.
 * Data source: salesByHour (24 elements, hours 0–23).
 * Shows orderCount, revenue and visitorCount per hour.
 */
export const SalesStrengthByHoursWidget: React.FC<SalesStrengthByHoursWidgetProps> = ({ data }) => {
  const { t } = useTranslation();
  const currency = getDisplayCurrency(data);

  const chartData = React.useMemo(() => {
    const hours: any[] = data?.salesByHour ?? [];
    if (hours.length === 0) return [];

    return hours
      .sort((a, b) => a.hour - b.hour)
      .map((item: any) => ({
        hour: `${String(item.hour).padStart(2, '0')}:00`,
        orders: item.orderCount ?? 0,
        revenue: getAmountForCurrency(item.amountByCurrency, currency),
        visitors: item.visitorCount ?? 0,
      }));
  }, [data, currency]);

  if (chartData.length === 0) {
    return (
      <div className="stat-card">
        <div className="stat-card-header">
          <h3 className="stat-card-title">{t('dashboard_stats_sales_by_hours')}</h3>
        </div>
        <p className="stat-metric-label">{t('dashboard_stats_no_data')}</p>
      </div>
    );
  }

  // Find peak hour
  const peakHour = chartData.reduce((best, curr) => (curr.revenue > best.revenue ? curr : best), chartData[0]);

  return (
    <div className="stat-card">
      <div className="stat-card-header">
        <h3 className="stat-card-title">{t('dashboard_stats_sales_by_hours')}</h3>
      </div>
      <div className="stat-metrics-row">
        <div className="stat-metric">
          <span className="stat-metric-label">{t('dashboard_stats_peak_hour')}</span>
          <span className="stat-metric-value">{peakHour.hour}</span>
        </div>
        <div className="stat-metric">
          <span className="stat-metric-label">{t('dashboard_stats_peak_revenue')}</span>
          <span className="stat-metric-value">{formatMoney(peakHour.revenue, currency)}</span>
        </div>
        <div className="stat-metric">
          <span className="stat-metric-label">{t('dashboard_stats_peak_orders')}</span>
          <span className="stat-metric-value">{peakHour.orders}</span>
        </div>
      </div>
      <div className="chart-container">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="hour" tick={{ fontSize: 9 }} stroke="#6B7280" interval={1} />
            <YAxis yAxisId="left" tick={{ fontSize: 10 }} stroke="#6B7280" />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} stroke="#6B7280" />
            <Tooltip
              formatter={(value: number, name: string) => {
                if (name === 'revenue') return [formatMoney(value, currency), t('dashboard_stats_revenue')];
                if (name === 'orders') return [value, t('dashboard_stats_orders')];
                if (name === 'visitors') return [value.toLocaleString(), t('dashboard_stats_visitors')];
                return [value, name];
              }}
            />
            <Legend
              formatter={(value: string) => {
                if (value === 'revenue') return t('dashboard_stats_revenue');
                if (value === 'orders') return t('dashboard_stats_orders');
                if (value === 'visitors') return t('dashboard_stats_visitors');
                return value;
              }}
            />
            <Bar yAxisId="right" dataKey="visitors" fill="#9CA3AF" opacity={0.4} radius={[2, 2, 0, 0]} />
            <Bar yAxisId="left" dataKey="revenue" fill="#2563EB" radius={[2, 2, 0, 0]} />
            <Bar yAxisId="left" dataKey="orders" fill="#059669" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
